/**
 * Calls the backend API procedure endpoint with the given endpoint and input data.
 * @param {string} endpoint - The procedure endpoint to call (e.g., "login", "register").
 * @param {object} inputData - The input JSON object to send in the request body.
 * @returns {Promise<object>} - The response JSON from the API.
 */

import { BACKEND_URL, BACKEND_URL_FALLBACK } from './config/backendConfig';

export async function fetchData(endpoint, inputData = {}) {
  // Try configured endpoint first, fallback to localhost if it fails
  const urls = [
    BACKEND_URL,
    BACKEND_URL_FALLBACK
  ];
  
  let lastError;
  
  for (const backendUrl of urls) {
    try {
      const url = `${backendUrl}/api/procedures/${endpoint}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(inputData)
      });
      
      if (response.ok) {
        return response.json();
      } else {
        const error = await response.json().catch(() => ({}));
        lastError = new Error(error.error || "API call failed");
        // If it's a server error (5xx), try the next URL
        if (response.status >= 500) {
          continue;
        }
        // If it's a client error (4xx), don't try other URLs
        throw lastError;
      }
    } catch (error) {
      lastError = error;
      // If it's a network error (connection refused, timeout, etc.), try the next URL
      if (error.name === 'TypeError' || error.message.includes('fetch')) {
        continue;
      }
      // For other errors, don't try other URLs
      throw error;
    }
  }
  
  // If we get here, all URLs failed
  throw lastError || new Error("All backend endpoints failed");
}